import React from "react";
import Button from "../../Button";
import { ResearchChart, ResearchFigureSpec, ResearchFlow, ResearchOrg, ResearchOrgNode, ResearchRangeFigure, ResearchTimeline } from "../../../types/bd/Research";
import classes from "./researchFigure.module.scss";

/**
 * A chart or diagram on a research page, drawn by CRX from what the AI
 * described (so every picture looks the same and downloads as PNG):
 * horizontal bars, columns, a timeline, a flowchart, an org chart, or a
 * range check (CRX's own figures).
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayMs = 86400000;
const utc = (iso: string): number => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d); };
const dayLabel = (ms: number): string => { const t = new Date(ms); return `${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`; };

/** Cut a label to n characters with an ellipsis. */
export const cut = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Up to three lines of about `max` characters. */
export function wrapLines(text: string, max: number): string[] {
    const lines: string[] = [];
    let cur = "";
    String(text).split(/\s+/).forEach((w) => {
        if ((`${cur} ${w}`).trim().length > max && cur) { lines.push(cur); cur = w; } else cur = `${cur} ${w}`.trim();
    });
    if (cur) lines.push(cur);
    return lines.slice(0, 3);
}

/** The next round number at or above v (1, 2, 2.5, 5, 10 × a power of ten), for axis tops. */
export function niceCeil(v: number): number {
    if (v <= 0) return 1;
    const e = Math.pow(10, Math.floor(Math.log10(v)));
    const f = v / e;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * e;
}

const num = (v: number): string => v.toLocaleString("en-US", { maximumFractionDigits: 2 });
/** A currency code's prefix ("JOD "), else "". */
const codeOf = (unit: string): string => (/^[A-Z]{3}$/.test(unit) ? `${unit} ` : "");
/** A value with its unit: "$1,200,000", "+4.5%", "JOD 1,200.5", "38". */
export const withUnit = (v: number, unit: ResearchChart["unit"]): string =>
    unit === "%" ? `${v > 0 ? "+" : ""}${v.toFixed(1)}%` : unit === "$" ? `${v < 0 ? "-" : ""}$${num(Math.abs(Math.round(v)))}` : `${codeOf(unit)}${num(v)}`;
/** A short value for axes and column tops: "$1.2M", "$640k", "JOD 1.2k", "38". */
export function shortValue(v: number, unit: ResearchChart["unit"]): string {
    const a = Math.abs(v);
    const p = unit === "$" ? "$" : codeOf(unit);
    if (a >= 1e6) return `${v < 0 ? "-" : ""}${p}${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
    if (a >= 1e3) return `${v < 0 ? "-" : ""}${p}${Math.round(a / 1e3)}k`;
    return unit === "%" ? withUnit(v, "%") : `${v < 0 ? "-" : ""}${p}${num(a)}`;
}

const Svg: React.FC<{ id: string; w: number; h: number; title: string; scroll?: boolean; children: React.ReactNode }> = ({ id, w, h, title, scroll, children }) => (
    <svg id={id} viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg"
        style={{ width: "100%", maxWidth: w, minWidth: scroll ? Math.min(w, 620) : undefined }}>
        {children}
    </svg>
);

const Bars: React.FC<{ spec: ResearchChart; id: string }> = ({ spec, id }) => {
    const W = 720; const labelW = 236; const rowH = 30; const top = 44;
    const vals = spec.items.map((i) => i.value);
    const min = Math.min(0, ...vals); const max = Math.max(0, ...vals);
    const padL = min < 0 ? 70 : 0; const padR = 90;
    const x0 = labelW + padL; const plotW = W - x0 - padR; const span = (max - min) || 1;
    const sx = (v: number) => x0 + ((v - min) / span) * plotW;
    const H = top + spec.items.length * rowH + 14;
    return (
        <Svg id={id} w={W} h={H} title={spec.title}>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 90)}</text>
            {spec.items.map((it, i) => {
                const y = top + i * rowH; const a = sx(0); const b = sx(it.value);
                return (
                    <g key={i}>
                        {i % 2 === 0 && <rect className={classes.sBand} x={0} y={y} width={W} height={rowH} />}
                        <text className={classes.sLabel} x={labelW - 10} y={y + 19} textAnchor="end">{cut(it.label, 34)}</text>
                        <rect className={it.value < 0 ? classes.fNeg : classes.fMain} x={Math.min(a, b)} y={y + 8} width={Math.max(1.5, Math.abs(b - a))} height={14} rx={2} />
                        <text className={classes.sStrong} x={it.value >= 0 ? b + 6 : b - 6} y={y + 19} textAnchor={it.value >= 0 ? "start" : "end"}>{withUnit(it.value, spec.unit)}</text>
                    </g>
                );
            })}
            <line className={classes.sAxis} x1={sx(0)} y1={top - 4} x2={sx(0)} y2={H - 8} />
        </Svg>
    );
};

const Columns: React.FC<{ spec: ResearchChart; id: string }> = ({ spec, id }) => {
    const W = 720; const H = 300; const padL = 64; const padB = 44; const padT = 44; const padR = 16;
    const positives = spec.items.map((i) => Math.max(0, i.value));
    const max = niceCeil(Math.max(...positives, 0));
    const plotW = W - padL - padR; const plotH = H - padT - padB;
    const step = plotW / Math.max(1, spec.items.length); const bw = Math.min(64, step * 0.6);
    return (
        <Svg id={id} w={W} h={H} title={spec.title}>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 90)}</text>
            {[0, 1, 2, 3, 4].map((k) => {
                const v = (max / 4) * k; const y = padT + plotH - (v / max) * plotH;
                return (
                    <g key={k}>
                        <line className={classes.sGrid} x1={padL} y1={y} x2={W - padR} y2={y} />
                        <text className={classes.sMuted} x={padL - 8} y={y + 4} textAnchor="end">{shortValue(v, spec.unit)}</text>
                    </g>
                );
            })}
            {spec.items.map((it, i) => {
                const h = Math.max(1, (Math.max(0, it.value) / max) * plotH); const x = padL + step * i + (step - bw) / 2; const y = padT + plotH - h;
                return (
                    <g key={i}>
                        <rect className={it.value < 0 ? classes.fNeg : classes.fMain} x={x} y={y} width={bw} height={h} rx={2} />
                        <text className={classes.sStrong} x={x + bw / 2} y={y - 6} textAnchor="middle">{shortValue(it.value, spec.unit)}</text>
                        <text className={classes.sLabel} x={x + bw / 2} y={padT + plotH + 18} textAnchor="middle">{cut(it.label, 14)}</text>
                    </g>
                );
            })}
            <line className={classes.sAxis} x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} />
        </Svg>
    );
};

const Timeline: React.FC<{ spec: ResearchTimeline; id: string; today?: string }> = ({ spec, id, today }) => {
    const W = 760; const labelW = 220; const rowH = 28; const top = 76; const padR = 90;
    const starts = spec.items.map((i) => utc(i.start));
    const ends = spec.items.map((i) => utc(i.end || i.start));
    const t0 = Math.min(...starts) - 3 * dayMs; const t1 = Math.max(...ends) + 4 * dayMs;
    const sx = (t: number) => labelW + ((t - t0) / (t1 - t0)) * (W - labelW - padR);
    const H = top + spec.items.length * rowH + 16;
    // long spans label quarters or years so month labels don't collide
    const months = (t1 - t0) / (30.44 * dayMs);
    const step = months <= 14 ? 1 : months <= 40 ? 3 : 12;
    const ticks: Array<{ x: number; label: string }> = [];
    const first = new Date(t0);
    for (let m = Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 1); m < t1;) {
        const d = new Date(m); const mi = d.getUTCMonth();
        if (step === 1 || (step === 3 && mi % 3 === 0) || (step === 12 && mi === 0)) ticks.push({ x: sx(m), label: step === 12 ? String(d.getUTCFullYear()) : `${MONTHS[mi]} ${d.getUTCFullYear()}` });
        m = Date.UTC(d.getUTCFullYear(), mi + 1, 1);
    }
    const todayMs = today ? utc(today) : NaN;
    return (
        <Svg id={id} w={W} h={H} title={spec.title} scroll>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 90)}</text>
            {ticks.map((t, i) => (
                <g key={i}>
                    <line className={classes.sGrid} x1={t.x} y1={top - 14} x2={t.x} y2={H - 8} />
                    <text className={classes.sMuted} x={t.x + 4} y={top - 18}>{t.label}</text>
                </g>
            ))}
            {spec.items.map((it, i) => {
                const y = top + i * rowH; const a = sx(utc(it.start));
                return (
                    <g key={i}>
                        {i % 2 === 0 && <rect className={classes.sBand} x={0} y={y} width={W} height={rowH} />}
                        <text className={classes.sLabel} x={labelW - 12} y={y + 18} textAnchor="end">{cut(it.label, 34)}</text>
                        {it.end ? (
                            <>
                                <rect className={classes.fBar} x={a} y={y + 8} width={Math.max(3, sx(utc(it.end)) - a)} height={12} rx={3} />
                                <text className={classes.sMuted} x={sx(utc(it.end)) + 6} y={y + 18}>{dayLabel(utc(it.start))}–{dayLabel(utc(it.end))}</text>
                            </>
                        ) : (
                            <>
                                <rect className={classes.fMain} x={a - 6} y={y + 8} width={12} height={12} transform={`rotate(45 ${a} ${y + 14})`} />
                                <text className={classes.sMuted} x={a + 12} y={y + 18}>{dayLabel(utc(it.start))}</text>
                            </>
                        )}
                    </g>
                );
            })}
            {todayMs > t0 && todayMs < t1 && (
                <>
                    <line className={classes.sToday} x1={sx(todayMs)} y1={top - 30} x2={sx(todayMs)} y2={H - 6} />
                    <text className={classes.sMuted} x={sx(todayMs)} y={top - 34} textAnchor="middle">Today, {dayLabel(todayMs)}</text>
                </>
            )}
        </Svg>
    );
};

interface PlacedNode { id: string; label: string; kind: ResearchFlow["nodes"][number]["kind"]; col: number; row: number; }

/** Columns by longest path from a start, rows in order within a column. */
export function layoutFlow(spec: ResearchFlow): PlacedNode[] {
    const rank: Record<string, number> = {};
    spec.nodes.forEach((n) => { rank[n.id] = 0; });
    for (let pass = 0; pass < spec.nodes.length; pass++) {
        spec.edges.forEach((e) => {
            if (rank[e.from] !== undefined && rank[e.to] !== undefined && rank[e.to] < rank[e.from] + 1 && rank[e.from] + 1 < spec.nodes.length) rank[e.to] = rank[e.from] + 1;
        });
    }
    const used: Record<number, number> = {};
    return spec.nodes.map((n) => { const col = rank[n.id]; used[col] = (used[col] ?? 0) + 1; return { ...n, col, row: used[col] - 1 }; });
}

/** Room between columns: arrow labels sit in it (up to 28px after a box), so it grows with the longest label instead of running under the next box. */
export const flowGap = (spec: ResearchFlow): number => Math.max(34, Math.max(0, ...spec.edges.map((e) => cut(e.label, 14).length)) * 6.6 + 28);

const Flow: React.FC<{ spec: ResearchFlow; id: string }> = ({ spec, id }) => {
    const nodes = layoutFlow(spec);
    const byId: Record<string, PlacedNode> = {};
    nodes.forEach((n) => { byId[n.id] = n; });
    const nw = 148; const colW = nw + flowGap(spec); const rowH = 112; const nh = 58; const pad = 14; const top = 34;
    const maxC = Math.max(0, ...nodes.map((n) => n.col)); const maxR = Math.max(0, ...nodes.map((n) => n.row));
    const W = pad * 2 + maxC * colW + nw; const H = top + pad * 2 + maxR * rowH + 76;
    const cx = (n: PlacedNode) => pad + n.col * colW + nw / 2;
    const cy = (n: PlacedNode) => top + pad + n.row * rowH + 38;
    const half = (n: PlacedNode) => (n.kind === "decision" ? { w: 66, h: 38 } : { w: nw / 2, h: nh / 2 });
    const arrow = `${id}-arrow`;
    return (
        <Svg id={id} w={W} h={H} title={spec.title} scroll>
            <defs>
                <marker id={arrow} viewBox="0 0 10 10" refX={9} refY={5} markerWidth={7} markerHeight={7} orient="auto-start-reverse">
                    <path className={classes.sArrow} d="M0,0 L10,5 L0,10 z" />
                </marker>
            </defs>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 100)}</text>
            {spec.edges.map((e, i) => {
                const a = byId[e.from]; const b = byId[e.to];
                if (!a || !b) return null;
                const ha = half(a); const hb = half(b);
                let d: string; let lx: number; let ly: number; let anchor: "start" | "middle" = "middle";
                if (b.col > a.col && b.row === a.row) { d = `M${cx(a) + ha.w},${cy(a)} H${cx(b) - hb.w}`; lx = (cx(a) + ha.w + cx(b) - hb.w) / 2; ly = cy(a) - 7; }
                else if (b.col === a.col) { const down = b.row > a.row; d = `M${cx(a)},${cy(a) + (down ? ha.h : -ha.h)} V${cy(b) + (down ? -hb.h : hb.h)}`; lx = cx(a) + 8; ly = (cy(a) + cy(b)) / 2 + 4; anchor = "start"; }
                else if (b.col > a.col) { const mx = cx(a) + ha.w + 14; d = `M${cx(a) + ha.w},${cy(a)} H${mx} V${cy(b)} H${cx(b) - hb.w}`; lx = mx + 6; ly = (cy(a) + cy(b)) / 2 + 4; anchor = "start"; }
                else { const y = top + pad + (maxR + 1) * rowH - 20; d = `M${cx(a)},${cy(a) + ha.h} V${y} H${cx(b)} V${cy(b) + hb.h}`; lx = (cx(a) + cx(b)) / 2; ly = y - 6; }
                return (
                    <g key={i}>
                        <path className={classes.sEdge} d={d} markerEnd={`url(#${arrow})`} />
                        {e.label && <text className={classes.sEdgeLabel} x={lx} y={ly} textAnchor={anchor}>{cut(e.label, 14)}</text>}
                    </g>
                );
            })}
            {nodes.map((n) => {
                const x = cx(n); const y = cy(n); const lines = wrapLines(n.label, n.kind === "decision" ? 14 : 20);
                return (
                    <g key={n.id}>
                        {n.kind === "decision"
                            ? <polygon className={classes.nDecision} points={`${x},${y - 38} ${x + 66},${y} ${x},${y + 38} ${x - 66},${y}`} />
                            : <rect className={n.kind === "start" || n.kind === "end" ? classes.nEnd : classes.nStep} x={x - nw / 2} y={y - nh / 2} width={nw} height={nh} rx={n.kind === "step" ? 8 : nh / 2} />}
                        {lines.map((l, k) => <text key={k} className={classes.sLabel} x={x} y={y + 4 + (k - (lines.length - 1) / 2) * 14} textAnchor="middle">{l}</text>)}
                    </g>
                );
            })}
        </Svg>
    );
};

interface PlacedPerson { n: ResearchOrgNode; x: number; y: number; kids: PlacedPerson[]; }

/** Leaves left to right, each manager centred over their team. */
export function layoutOrg(root: ResearchOrgNode): { placed: PlacedPerson[]; leaves: number } {
    const nw = 178; const nh = 60; const hg = 18; const vg = 64; const top = 36; const pad = 8;
    let leaf = 0;
    const placed: PlacedPerson[] = [];
    const walk = (n: ResearchOrgNode, depth: number): PlacedPerson => {
        const kids = (n.children ?? []).map((c) => walk(c, depth + 1));
        const x = kids.length ? (kids[0].x + kids[kids.length - 1].x) / 2 : pad + (leaf++) * (nw + hg) + nw / 2;
        const node = { n, x, y: top + depth * (nh + vg), kids };
        placed.push(node);
        return node;
    };
    walk(root, 0);
    return { placed, leaves: leaf };
}

const Org: React.FC<{ spec: ResearchOrg; id: string }> = ({ spec, id }) => {
    const nw = 178; const nh = 60; const hg = 18; const vg = 64; const pad = 8;
    const { placed, leaves } = layoutOrg(spec.root);
    const W = Math.max(220, pad * 2 + leaves * (nw + hg) - hg);
    const H = Math.max(...placed.map((p) => p.y)) + nh + pad;
    return (
        <Svg id={id} w={W} h={H} title={spec.title} scroll>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 100)}</text>
            {placed.flatMap((p) => p.kids.map((k, i) => (
                <path key={`${p.n.name}-${i}`} className={classes.sEdge} d={`M${p.x},${p.y + nh} V${p.y + nh + vg / 2} H${k.x} V${k.y}`} />
            )))}
            {placed.map((p, i) => (
                <g key={i}>
                    <rect className={classes.nStep} x={p.x - nw / 2} y={p.y} width={nw} height={nh} rx={8} />
                    <text className={classes.sStrong} x={p.x} y={p.y + 24} textAnchor="middle">{cut(p.n.name, 24)}</text>
                    <text className={classes.sMuted} x={p.x} y={p.y + 42} textAnchor="middle">{cut(p.n.role, 28)}</text>
                </g>
            ))}
        </Svg>
    );
};

/** Where a figure sits: the middle half shaded, the median, every number as a dot, the figure marked in gold. */
const Range: React.FC<{ spec: ResearchRangeFigure; id: string }> = ({ spec, id }) => {
    const r = spec.range;
    const W = 720; const H = 146; const x0 = 40; const x1 = W - 40;
    const lo = Math.min(r.min, r.value); const hi = Math.max(r.max, r.value);
    const sx = (v: number) => x0 + ((v - lo) / ((hi - lo) || 1)) * (x1 - x0);
    const anchor = (x: number): "start" | "middle" | "end" => (x < x0 + 60 ? "start" : x > x1 - 60 ? "end" : "middle");
    const fmt = (v: number) => withUnit(v, "");
    const mark = sx(r.value);
    return (
        <Svg id={id} w={W} h={H} title={spec.title}>
            <text className={classes.sTitle} x={0} y={18}>{cut(spec.title, 90)}</text>
            <rect className={classes.nDecision} x={sx(r.q1)} y={54} width={Math.max(2, sx(r.q3) - sx(r.q1))} height={26} rx={3} />
            <line className={classes.sEdge} x1={sx(r.median)} y1={48} x2={sx(r.median)} y2={86} />
            <text className={classes.sMuted} x={sx(r.median)} y={42} textAnchor={anchor(sx(r.median))}>Median {fmt(r.median)}</text>
            {r.values.map((v, i) => <circle key={i} className={classes.fBar} cx={sx(v)} cy={67} r={4} />)}
            <line className={classes.sAxis} x1={x0} y1={100} x2={x1} y2={100} />
            <text className={classes.sMuted} x={sx(r.min)} y={120} textAnchor={anchor(sx(r.min))}>Lowest {fmt(r.min)}</text>
            <text className={classes.sMuted} x={sx(r.max)} y={120} textAnchor={anchor(sx(r.max))}>Highest {fmt(r.max)}</text>
            <line className={classes.sMark} x1={mark} y1={50} x2={mark} y2={100} />
            <rect className={classes.fMain} x={mark - 6} y={94} width={12} height={12} transform={`rotate(45 ${mark} 100)`} />
            <text className={classes.sStrong} x={mark} y={140} textAnchor={anchor(mark)}>{cut(r.label, 40)}: {fmt(r.value)}</text>
        </Svg>
    );
};

const KIND: Record<string, { icon: string; label: string }> = {
    bars: { icon: "bar-chart", label: "Chart" }, columns: { icon: "bar-chart", label: "Chart" },
    timeline: { icon: "calendar-range", label: "Timeline" }, flow: { icon: "diagram-3", label: "Flowchart" }, org: { icon: "people", label: "Org chart" },
    range: { icon: "rulers", label: "Range check" },
};

/** "Chart", "Timeline", "Flowchart", "Org chart". */
export const figureLabel = (spec: ResearchFigureSpec): string => KIND[spec.type]?.label ?? "Diagram";

interface ResearchFigureProps {
    spec: ResearchFigureSpec;
    /** The SVG's id: the PNG and PDF exports find the picture by it. */
    svgId: string;
    /** Marks today on a timeline; left out on pictures that go to a client. */
    today?: string;
    onDownload?: () => void;
}

/** One chart or diagram with its caption and a Download PNG button. */
const ResearchFigure: React.FC<ResearchFigureProps> = ({ spec, svgId, today, onDownload }) => {
    const kind = KIND[spec.type] ?? KIND.bars;
    const hasNegative = (spec.type === "bars" || spec.type === "columns") && spec.items.some((i) => i.value < 0);
    return (
        <figure className={classes.figure}>
            <figcaption className={classes.caption}>
                <span className={classes.captionTitle}><i className={`bi bi-${kind.icon}`} aria-hidden="true" />{kind.label}</span>
                {hasNegative && <span className={classes.legend}><span><i className={classes.swatchMain} />Up</span><span><i className={classes.swatchNeg} />Down</span></span>}
                {onDownload && <Button size="xs" variant="ghost" icon="download" onClick={onDownload} title={`Download this ${kind.label.toLowerCase()} as a PNG image (light colours, for documents)`}>Download PNG</Button>}
            </figcaption>
            <div className={classes.scroll}>
                {spec.type === "bars" && <Bars spec={spec} id={svgId} />}
                {spec.type === "columns" && <Columns spec={spec} id={svgId} />}
                {spec.type === "timeline" && <Timeline spec={spec} id={svgId} today={today} />}
                {spec.type === "flow" && <Flow spec={spec} id={svgId} />}
                {spec.type === "org" && <Org spec={spec} id={svgId} />}
                {spec.type === "range" && <Range spec={spec} id={svgId} />}
            </div>
        </figure>
    );
};

export default ResearchFigure;
